import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CodeMirror from '@uiw/react-codemirror';
import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { api, FileMeta } from '../lib/api';
import { ManifestForms } from '../components/ManifestForms';
import { ErrorState } from '../components/QueryState';
import { parseYaml, stringifyYaml, validateManifestDoc, ManifestDoc, emptyManifest } from '../lib/manifestModel';

export function Manifest() {
  const qc = useQueryClient();
  const { data: manifest, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['manifest', 'global'],
    queryFn: () => api.get<{ yaml: string; updated_at: number | null }>('/manifest/global'),
  });
  const { data: filesList } = useQuery({
    queryKey: ['files'],
    queryFn: () => api.get<{ files: FileMeta[] }>('/files/'),
  });
  // Show the pinned signing key id alongside the manifest editor so admins
  // know which key the bundle they push needs to match.
  const { data: pubkey } = useQuery({
    queryKey: ['signed-bundle', 'pubkey'],
    queryFn: () => api.get<{ key_id: string; pubkey_pem: string }>('/signed-bundle/pubkey'),
  });

  const [tab, setTab] = useState<'forms' | 'yaml'>('forms');
  const [text, setText] = useState<string>('');
  const [doc, setDoc] = useState<ManifestDoc>(emptyManifest());
  const [parseError, setParseError] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  useEffect(() => {
    if (manifest?.yaml) {
      setText(manifest.yaml);
      const parsed = parseYaml(manifest.yaml);
      if (parsed.ok) {
        setDoc(parsed.doc);
        setParseError(null);
      } else {
        setParseError(parsed.error);
      }
    }
  }, [manifest?.yaml]);

  const save = useMutation({
    mutationFn: (yamlText: string) => api.put('/manifest/global', { yaml: yamlText }),
    onSuccess: () => {
      setSaveErrors(null);
      qc.invalidateQueries({ queryKey: ['manifest', 'global'] });
      qc.invalidateQueries({ queryKey: ['client'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['signed-bundle'] });
    },
    onError: (e: Error & { details?: { errors?: Array<{ path: string; message: string }> } }) => {
      setSaveErrors(
        e.details?.errors ? e.details.errors.map((x) => `${x.path}: ${x.message}`).join('\n') : e.message
      );
    },
  });

  const validationErrors = validateManifestDoc(doc);
  const updateDoc = (next: ManifestDoc) => {
    save.reset();
    setSaveErrors(null);
    setDoc(next);
  };

  function showValidationErrors(errors: string[]) {
    setSaveErrors(errors.join('\n'));
  }

  function switchToForms() {
    const parsed = parseYaml(text);
    if (!parsed.ok) {
      setParseError(parsed.error);
      return;
    }
    setParseError(null);
    setDoc(parsed.doc);
    save.reset();
    setTab('forms');
  }

  function switchToYaml() {
    setText(stringifyYaml(doc));
    save.reset();
    setTab('yaml');
  }

  function saveForms() {
    if (validationErrors.length) {
      showValidationErrors(validationErrors);
      return;
    }
    const y = stringifyYaml(doc);
    setText(y);
    save.mutate(y);
  }
  function saveYaml() {
    const parsed = parseYaml(text);
    if (!parsed.ok) {
      setParseError(parsed.error);
      return;
    }
    const errors = validateManifestDoc(parsed.doc);
    if (errors.length) {
      setDoc(parsed.doc);
      showValidationErrors(errors);
      return;
    }
    setParseError(null);
    setDoc(parsed.doc);
    save.mutate(text);
  }

  if (isLoading) return <div className="text-muted">Loading…</div>;
  if (isError) return <ErrorState error={error} onRetry={() => refetch()} />;

  function applyInventoryPreset() {
    const next: ManifestDoc = {
      version: 1,
      files: doc.files.some((f) => f.name === 'host-inventory')
        ? doc.files
        : [
            ...doc.files,
            {
              name: 'host-inventory',
              src: 'usr/local/bin/host-inventory',
              dest: '/usr/local/bin/host-inventory',
              mode: '0755',
              owner: 'root:root',
            },
          ],
      actions: doc.actions.some((a) => a.name === 'inventory')
        ? doc.actions
        : [
            ...doc.actions,
            { name: 'inventory', when: 'always', command: 'bash /usr/local/bin/host-inventory' },
          ],
    };
    save.reset();
    setSaveErrors(null);
    setDoc(next);
    if (tab === 'yaml') setText(stringifyYaml(next));
  }
  const presetApplied =
    doc.files.some((f) => f.name === 'host-inventory') && doc.actions.some((a) => a.name === 'inventory');

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="text-xl font-semibold">Global manifest</h1>
        {pubkey && (
          <div className="text-xs text-muted">
            Signed by <code>{pubkey.key_id}</code>{' '}
            <Link to="/signing" className="text-accent hover:underline">(Signing)</Link>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted">Presets:</span>
        <button className="btn" onClick={applyInventoryPreset} disabled={presetApplied}>
          {presetApplied ? '✓ Inventory action added' : '+ Add inventory action'}
        </button>
        <span className="text-xs text-muted">
          Adds the built-in <code>host-inventory</code> script and an <code>always</code> action that runs it each tick.
        </span>
      </div>
      <div className="border-b border-border flex gap-2 text-sm">
        <button className={tabClass(tab === 'forms')} onClick={switchToForms}>Forms</button>
        <button className={tabClass(tab === 'yaml')} onClick={switchToYaml}>YAML</button>
      </div>
      {parseError && <div className="text-danger text-sm">YAML parse error: {parseError}</div>}
      {tab === 'forms' && validationErrors.length > 0 && (
        <pre className="text-danger text-xs whitespace-pre-wrap">{validationErrors.join('\n')}</pre>
      )}
      {tab === 'forms' && (
        <>
          <ManifestForms doc={doc} setDoc={updateDoc} files={filesList?.files || []} />
          <div className="flex items-center gap-2">
            <button className="btn btn-primary" onClick={saveForms} disabled={save.isPending || validationErrors.length > 0}>
              {save.isPending ? 'Saving…' : 'Save manifest'}
            </button>
            {save.isSuccess && <span className="text-success text-sm">Saved.</span>}
          </div>
        </>
      )}
      {tab === 'yaml' && (
        <>
          <CodeMirror value={text} height="480px" extensions={[yamlLang()]} onChange={(value) => { save.reset(); setSaveErrors(null); setText(value); }} />
          <div className="flex items-center gap-2">
            <button className="btn btn-primary" onClick={saveYaml} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save manifest'}
            </button>
            {save.isSuccess && <span className="text-success text-sm">Saved.</span>}
          </div>
        </>
      )}
      {saveErrors && <pre className="text-danger text-xs whitespace-pre-wrap">{saveErrors}</pre>}
    </div>
  );
}

function tabClass(active: boolean) {
  return active
    ? 'px-3 py-2 border-b-2 border-accent text-accent font-medium'
    : 'px-3 py-2 text-muted hover:text-fg';
}
